import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Formik,
  Form,
} from 'formik';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  LISTING_CMA_DISCUSSION_ACKNOWLEDGED,
  LISTING_CMA_DISCUSSION_AGREED,
  LISTING_CMA_DISCUSSION_NOT_DISCUSSED,
  LISTING_CMA_DISCUSSION_PLACEHOLDER_URL_IMAGE,
} from 'common/listingConstants';

import {
  SR_API_ACTION_ADMIN_UPDATE_CMA_DISCUSSION,
} from 'api/actionTypes';

import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';

import SrDarkModal from 'client/components/SrDarkModal';
import SrBox from 'client/components/SrBox';
import SrModalContent from 'client/components/SrModalContent';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';

import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';

import './SrCMADiscussionModal.less';

const getFormFields = ({ formValues }) => {
  const shouldHideCMADiscussionNotes = typeof formValues.cmaDiscussionAgreed !== 'boolean'
    || formValues.cmaDiscussionAgreed;

  return [
    {
      id: 'cmaDiscussionAgreed',
      title: '',
      type: 'yes_no',
    },
    hideFieldIf(shouldHideCMADiscussionNotes, {
      id: 'cmaDiscussionNotes',
      title: 'Why is the partner not willing to match the CMA price?',
      titleMultiline: true,
      type: 'long_text',
      validations: ['required'],
    }),
  ];
};

@withApiState({
  updateCmaDiscussionApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_CMA_DISCUSSION,
  },
})
class SrCMADiscussionModal extends Component {
  static propTypes = {
    listingId: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    cmaDiscussed: PropTypes.string.isRequired,
    cmaDiscussionNotes: PropTypes.string,
  }

  _onSubmitForm = (formValues) => {
    const {
      listingId,
      dispatch,
      onClose,
      onSuccess: onSubmitSuccess,
    } = this.props;

    const formCMADiscussed = formValues.cmaDiscussionAgreed
      ? LISTING_CMA_DISCUSSION_AGREED
      : LISTING_CMA_DISCUSSION_ACKNOWLEDGED;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_CMA_DISCUSSION,
      payload: setNullEmptyValues({
        listingId: listingId,
        comparativeMarketAnalysisDiscussed: formCMADiscussed,
        comparativeMarketAnalysisDiscussionNotes: formValues.cmaDiscussionNotes,
      }),
      onSuccess: () => {
        onSubmitSuccess();
        onClose();
      },
    });
  };

  render() {
    const {
      cmaDiscussed,
      cmaDiscussionNotes,
      onClose,
      updateCmaDiscussionApiState,
    } = this.props;

    const initialFormValues = {
      cmaDiscussionAgreed: cmaDiscussed === LISTING_CMA_DISCUSSION_NOT_DISCUSSED
        ? null
        : cmaDiscussed,
      cmaDiscussionNotes,
    };

    return (
      <SrDarkModal
        onClose={onClose}
      >
        <SrModalContent
          style={{ maxWidth: 440 }}
        >
          <div className='SrCMADiscussionModal'>
            <SrFlex
              direction='col'
              align='center'
              marginTop='20px'
            >
              <SrBox
                width='207px'
                height='120px'
                borderRadius='18px'
                backgroundSize='cover'
                backgroundImage={`url(${LISTING_CMA_DISCUSSION_PLACEHOLDER_URL_IMAGE})`}
              />
              <SrBox
                marginTop='20px'
                maxWidth='334px'
              >
                <SrText
                  font='greycliff'
                  weight='800'
                  fontSize='26px'
                  lineHeight='33px'
                  display='block'
                  align='center'
                >
                  Is the partner willing to match the CMA price?
                </SrText>
              </SrBox>
            </SrFlex>
            <Formik
              initialValues={createInitialValues({
                formFields: getFormFields({
                  formValues: initialFormValues,
                }),
                defaultValues: initialFormValues,
              })}
              validate={(formValues) => {
                const formFields = getFormFields({
                  formValues,
                });
                return createFormValidationFn(formFields)(formValues);
              }}
              onSubmit={this._onSubmitForm}
              render={(formikBag) => {
                const formFields = getFormFields({
                  formValues: formikBag.values,
                });
                const getFormFieldsById = createFormFieldsByIdGetter(formFields);

                return (
                  <Form>
                    <SrFormikField
                      formField={getFormFieldsById('cmaDiscussionAgreed')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrFormikField
                      formField={getFormFieldsById('cmaDiscussionNotes')}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                    <SrBox
                      marginTop='20px'
                    >
                      <SrButton
                        type='submit'
                        onClick={formikBag.handleSubmit}
                        disabled={
                          !formikBag.isValid
                          && updateCmaDiscussionApiState.inProgress
                        }
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                      >
                        {updateCmaDiscussionApiState.inProgress
                          ? 'Please wait...'
                          : 'Submit'
                        }
                      </SrButton>
                    </SrBox>
                  </Form>
                );
              }}
            />
          </div>
        </SrModalContent>
      </SrDarkModal>
    );
  }
}

SrCMADiscussionModal.displayName = 'SrCMADiscussionModal';

export default SrCMADiscussionModal;
